"use client"

import { useEffect, useMemo, useRef, useState } from "react"
import { usePathname } from "next/navigation"
import { MOBILE_FLOATING_CONTACT_CLASS } from "@modules/layout/utils/mobile-fixed-offsets"
import { buildMessengerHref, buildWhatsAppHref } from "./helpers"

type Props = {
  enabled?: boolean
  facebookUrl?: string | null
  whatsappNumber?: string | null
}

function WhatsAppIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="h-5 w-5">
      <path
        fill="currentColor"
        d="M20.5 3.5A11.8 11.8 0 0 0 12.1 0C5.6 0 .3 5.3.3 11.8c0 2.1.5 4.1 1.6 5.9L0 24l6.5-1.7c1.7.9 3.7 1.4 5.6 1.4 6.5 0 11.8-5.3 11.8-11.8 0-3.2-1.2-6.1-3.4-8.4ZM12.1 21.7c-1.8 0-3.5-.5-5-1.3l-.4-.2-3.8 1 1-3.7-.2-.4a9.8 9.8 0 0 1-1.5-5.2c0-5.4 4.4-9.8 9.9-9.8 2.6 0 5.1 1 7 2.9a9.8 9.8 0 0 1 2.9 7c-.1 5.3-4.5 9.7-9.9 9.7Zm5.4-7.4c-.3-.1-1.8-.9-2.1-1-.3-.1-.5-.1-.7.2-.2.3-.8 1-.9 1.1-.2.2-.3.2-.6.1-.3-.1-1.2-.4-2.3-1.4-.9-.8-1.4-1.7-1.6-2-.2-.3 0-.5.1-.6l.5-.6c.2-.2.2-.3.3-.5.1-.2.1-.4 0-.6-.1-.1-.7-1.7-.9-2.3-.2-.6-.5-.5-.7-.5h-.6c-.2 0-.6.1-.9.4-.3.3-1.1 1-1.1 2.5s1.1 3 1.3 3.2c.1.2 2.1 3.3 5.2 4.6.7.3 1.3.5 1.8.6.7.2 1.4.2 1.9.1.6-.1 1.8-.7 2-1.4.2-.7.2-1.3.2-1.4-.1-.2-.3-.3-.6-.5Z"
      />
    </svg>
  )
}

function MessengerIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="h-5 w-5">
      <path
        fill="currentColor"
        d="M12 0C5.2 0 0 5 0 11.7c0 3.5 1.4 6.5 3.8 8.6V24l3.5-1.9c1.4.4 3 .7 4.7.7 6.8 0 12-5 12-11.7S18.8 0 12 0Zm1.2 15.7-3.1-3.3-6.1 3.3 6.7-7.1 3.2 3.3L19.8 8l-6.6 7.7Z"
      />
    </svg>
  )
}

function ChatIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="h-5 w-5">
      <path
        d="M4.5 5.5h15v10h-8l-4.5 3v-3H4.5v-10Z"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.8"
      />
      <path
        d="M8 9.5h8M8 12.5h5"
        stroke="currentColor"
        strokeLinecap="round"
        strokeWidth="1.8"
      />
    </svg>
  )
}

function ArrowUpIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="h-4 w-4">
      <path
        d="M12 19V5M6.5 10.5 12 5l5.5 5.5"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
      />
    </svg>
  )
}

export default function FloatingContactLauncher({
  enabled = true,
  facebookUrl,
  whatsappNumber,
}: Props) {
  const pathname = usePathname()
  const rootRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)
  const [showTop, setShowTop] = useState(false)

  const links = useMemo(
    () => ({
      messenger: buildMessengerHref(facebookUrl),
      whatsapp: buildWhatsAppHref(whatsappNumber),
    }),
    [facebookUrl, whatsappNumber]
  )

  const actions = [
    links.whatsapp
      ? {
          href: links.whatsapp,
          label: "Chat on WhatsApp",
          className: "bg-[#25D366] text-white hover:bg-[#1ebe5d]",
          icon: <WhatsAppIcon />,
        }
      : null,
    links.messenger
      ? {
          href: links.messenger,
          label: "Message on Facebook",
          className: "bg-[#0084FF] text-white hover:bg-[#0676dd]",
          icon: <MessengerIcon />,
        }
      : null,
  ].filter(Boolean) as Array<{
    className: string
    href: string
    icon: React.ReactNode
    label: string
  }>

  const isProductPage = pathname?.includes("/products/")
  const containerClass = isProductPage
    ? "fixed bottom-[calc(10.25rem+env(safe-area-inset-bottom))] right-3 z-[80] flex flex-col items-end gap-2 small:bottom-5 small:right-5"
    : MOBILE_FLOATING_CONTACT_CLASS

  useEffect(() => {
    const handleScroll = () => setShowTop(window.scrollY > 360)
    handleScroll()
    window.addEventListener("scroll", handleScroll, { passive: true })
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  useEffect(() => {
    if (!open) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false)
    }

    const handlePointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false)
      }
    }

    document.addEventListener("keydown", handleKeyDown)
    document.addEventListener("pointerdown", handlePointerDown)
    return () => {
      document.removeEventListener("keydown", handleKeyDown)
      document.removeEventListener("pointerdown", handlePointerDown)
    }
  }, [open])

  if (!enabled || actions.length === 0) {
    return null
  }

  return (
    <div ref={rootRef} className={containerClass}>
      <button
        type="button"
        aria-label="Scroll to top"
        title="Scroll to top"
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        className={`flex h-9 w-9 items-center justify-center rounded-full bg-[#1A1A1A] text-white shadow-[0_10px_25px_-14px_rgba(26,26,26,0.8)] transition duration-200 hover:bg-[#3D5240] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3D5240] focus-visible:ring-offset-2 ${
          showTop
            ? "translate-y-0 opacity-100"
            : "pointer-events-none translate-y-2 opacity-0"
        }`}
      >
        <ArrowUpIcon />
      </button>

      <div className="flex flex-col items-end gap-2">
        {actions.map((action, index) => (
          <a
            key={action.label}
            href={action.href}
            target="_blank"
            rel="noreferrer"
            aria-label={action.label}
            title={action.label}
            onClick={() => setOpen(false)}
            className={`flex h-11 w-11 items-center justify-center rounded-full shadow-[0_12px_28px_-16px_rgba(26,26,26,0.85)] transition duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3D5240] focus-visible:ring-offset-2 ${action.className} ${
              open
                ? "translate-y-0 scale-100 opacity-100"
                : "pointer-events-none translate-y-3 scale-95 opacity-0"
            }`}
            style={{ transitionDelay: open ? `${index * 35}ms` : "0ms" }}
          >
            {action.icon}
          </a>
        ))}

        <button
          type="button"
          aria-label={open ? "Close contact options" : "Open contact options"}
          aria-expanded={open}
          title={open ? "Close contact options" : "Open contact options"}
          onClick={() => setOpen((current) => !current)}
          className="flex h-12 w-12 items-center justify-center rounded-full bg-[#3D5240] text-white shadow-[0_14px_32px_-16px_rgba(26,26,26,0.9)] transition duration-200 hover:bg-[#2f4032] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3D5240] focus-visible:ring-offset-2"
        >
          <span
            className={`transition duration-200 ${open ? "rotate-12 scale-95" : "rotate-0 scale-100"}`}
          >
            <ChatIcon />
          </span>
        </button>
      </div>
    </div>
  )
}
